/**
 * Daily Breakdown — column order:
 * Date → Sign up / Sign out → Total|Unique Attempt → Missed → Canceled → Completed →
 * 1st–4th Consultation (no Unique columns) → Same Day 1st→2nd & % →
 * 1st/2nd/≥3rd recharge (count & amount only) → Total Recharge|Unique Total Recharge Count → Total Spending (main only).
 */

const pair = (totalCol, uniqueCol) => [totalCol, uniqueCol];
const SEQUENCE_LABELS = ['', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th'];
const SEQUENCE_KEYS = ['', 'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh'];

const sameDayTransitionColumns = (kind, fromValues) => fromValues.flatMap((from) => {
  const prefix = `sameDay${SEQUENCE_KEYS[from]}To${SEQUENCE_KEYS[from + 1]}${kind}`;
  const subject = kind === 'Recharge' ? 'recharge' : 'consultation';
  return [
    {
      key: `${prefix}Count`,
      label: `Same Day ${SEQUENCE_LABELS[from]} to ${SEQUENCE_LABELS[from + 1]} ${kind} Count`,
      type: 'number',
      description: `Users whose lifetime ${SEQUENCE_LABELS[from]} and ${SEQUENCE_LABELS[from + 1]} ${subject}s happened on the same IST calendar date.`,
      example: `Both adjacent ${subject}s occur on one date → counted once.`,
    },
    {
      key: `${prefix}ConversionPct`,
      label: `Same Day ${SEQUENCE_LABELS[from]} to ${SEQUENCE_LABELS[from + 1]} ${kind} Conversion %`,
      type: 'percentage',
      description: `Same-day ${SEQUENCE_LABELS[from]}→${SEQUENCE_LABELS[from + 1]} count ÷ ${SEQUENCE_LABELS[from]} ${subject} count on that date × 100.`,
      example: `10 users at the ${SEQUENCE_LABELS[from]} stage and 3 reach the next stage the same day → 30%.`,
    },
  ];
});

export const DAILY_BREAKDOWN_COLUMN_DEFINITIONS = [
  {
    key: "date",
    label: "Date",
    type: "date",
    description:
      "Calendar day in Asia/Kolkata. One row per date in the selected range.",
    example: "12 Apr 2026 — all metrics below are for that full day only.",
  },
  {
    key: "totalSignup",
    label: "Total Sign up",
    type: "number",
    description:
      "Count of new user registrations completed on this date (successful sign-ups).",
    example: "If 8 users created an account on this day, this shows 8.",
  },
  {
    key: "totalSignout",
    label: "Total Sign out",
    type: "number",
    description:
      "Count of account deletions / sign-outs recorded on this date.",
    example: "If 2 users deleted their account that day, this shows 2.",
  },
  ...pair(
    {
      key: "totalConsultationAttempt",
      label: "Total Consultation Attempt",
      type: "number",
      metricGroupStart: true,
      description:
        "Counts every consultation attempt in the selected period for this date: all tries on the app (including multiple tries by the same customer). Same logic as other “Total” columns below.",
      example:
        "User A starts 2 attempts and User B starts 1 on this day → Total = 3 attempts.",
    },
    {
      key: "uniqueConsultationAttempt",
      label: "Unique Consultation Attempt",
      type: "number",
      className: "primary",
      description:
        "Counts unique customers only. If a customer re-attempts consultation multiple times on the same day, it still counts as 1 for this column. Same “unique customer per day” rule as Unique Missed / Canceled / Completed.",
      example:
        "Same 3 attempts as above, but only 2 different customers → Unique = 2.",
    }
  ),
  ...pair(
    {
      key: "totalMissedConsultation",
      label: "Total Missed Consultation",
      type: "number",
      className: "error",
      metricGroupStart: true,
      description:
        "Same theory as Total Consultation Attempt: every missed consultation event on this date counts (multiple misses by one customer each add to Total).",
      example:
        "Two missed calls for User A and one for User B → Total Missed = 3.",
    },
    {
      key: "uniqueMissedConsultation",
      label: "Unique Missed Consultation",
      type: "number",
      className: "error",
      description:
        "Same theory as Unique Consultation Attempt: one row per distinct customer per day for missed consultations (repeated misses same day still count as 1 customer).",
      example:
        "User A misses twice and User B once → Total can be 3, Unique = 2.",
    }
  ),
  ...pair(
    {
      key: "totalCanceledConsultation",
      label: "Total Canceled Consultation",
      type: "number",
      className: "error",
      metricGroupStart: true,
      description:
        "Same theory as Total Consultation Attempt: every cancellation event on this date counts toward Total.",
      example:
        "One user cancels 2 bookings and another cancels 1 → Total Canceled = 3.",
    },
    {
      key: "uniqueCanceledConsultation",
      label: "Unique Canceled Consultation",
      type: "number",
      className: "error",
      description:
        "Same theory as Unique Consultation Attempt: distinct customers who canceled at least once on this day (multiple cancels by same customer count as 1).",
      example:
        "3 cancel events but only 2 customers → Unique = 2.",
    }
  ),
  ...pair(
    {
      key: "totalCompletedConsultation",
      label: "Total Completed Consultation",
      type: "number",
      className: "success",
      metricGroupStart: true,
      description:
        "Same theory as Total Consultation Attempt: every completed consultation session on this date counts (free + paid completions; multiple completions by one customer each add to Total).",
      example:
        "4 completed sessions that day → Total Completed = 4.",
    },
    {
      key: "uniqueCompletedConsultation",
      label: "Unique Completed Consultation",
      type: "number",
      className: "success",
      description:
        "Same theory as Unique Consultation Attempt: distinct customers who completed at least one consultation on this day.",
      example:
        "4 completions but only 3 different customers → Unique Completed = 3.",
    }
  ),
  {
    key: "firstConsultation",
    label: "1st Consultation",
    type: "number",
    className: "primary",
    metricGroupStart: true,
    uniqueKey: "sameDayRegisteredFirstConsultation",
    badgeLabel: "Same day registered + 1st",
    description:
      "Users whose first lifetime completed private consultation occurred on this date. Query sessions are excluded; coupon code/type does not affect this metric.",
    example:
      "3 first-slot completions on this day → 3.",
  },
  {
    key: "uniqueFirstConsultation",
    inlineOnly: true,
    label: "Unique 1st Consultation",
    type: "number",
    className: "primary",
    description:
      "Distinct users who completed a 1st consultation bucket session on this date.",
    example:
      "If one user has multiple qualifying 1st rows on the same date, this counts that user once.",
  },
  {
    key: "secondConsultation",
    label: "2nd Consultation",
    type: "number",
    className: "primary",
    uniqueKey: "sameDayRegisteredSecondConsultation",
    badgeLabel: "Same day registered + 1st + 2nd",
    description:
      "Users whose second lifetime completed private consultation occurred on this date. Query sessions are excluded; coupon code/type does not affect this metric.",
    example:
      "Four 2nd-slot completions on this day → 4.",
  },
  {
    key: "uniqueSecondConsultation",
    inlineOnly: true,
    label: "Unique 2nd Consultation",
    type: "number",
    className: "primary",
    description:
      "Distinct users who completed a 2nd consultation bucket session on this date.",
    example:
      "4 second-slot completions from 3 different users -> 3.",
  },
  {
    key: "thirdConsultation",
    label: "3rd Consultation",
    type: "number",
    className: "primary",
    uniqueKey: "sameDayRegisteredThirdConsultation",
    badgeLabel: "Same day registered + 1st + 2nd + 3rd",
    description:
      "Completions in the 3rd lifetime slot (session after 2nd in the user’s chronological history).",
    example:
      "Two users complete their 3rd-ever session today → 2.",
  },
  {
    key: "uniqueThirdConsultation",
    inlineOnly: true,
    label: "Unique 3rd Consultation",
    type: "number",
    className: "primary",
    description:
      "Distinct users whose 3rd consultation bucket is counted on this date.",
    example:
      "If 2 users complete their 3rd session today -> 2.",
  },
  {
    key: "fourthConsultation",
    label: "4th & Onwards Consultation",
    type: "number",
    className: "primary",
    uniqueKey: "sameDayRegisteredFourthConsultation",
    badgeLabel: "Same day registered + 1st + 2nd + 3rd + 4th+",
    description:
      "All completed sessions from the 4th lifetime completion onward on this date (4th, 5th, 6th, …).",
    example:
      "A user’s 5th session today adds +1 to this bucket.",
  },
  {
    key: "uniqueFourthConsultation",
    inlineOnly: true,
    label: "Unique 4th & Onwards Consultation",
    type: "number",
    className: "primary",
    description:
      "Distinct users who completed at least one 4th-or-later consultation on this date.",
    example:
      "If one user completes both 4th and 5th sessions today, total can be 2 but unique is 1.",
  },
  {
    key: "queryCompletedConsultation",
    label: "Query Completed",
    type: "number",
    className: "primary",
    metricGroupStart: true,
    description:
      "Total query completed",
    // example:
    //   "5 query-type sessions completed today → 5.",
  },
  {
    key: "sameDayRepeatConsultation",
    label: "Same Day 1st to 2nd Conversion",
    type: "number",
    className: "primary",
    metricGroupStart: true,
    description:
      "Formerly “Same day Repeat Consultation”. Count of users who completed both their 1st and their 2nd consultation on the same calendar day.",
    example:
      "User finishes 1st chat in the morning and 2nd chat the same day → +1 here.",
  },
  {
    key: "sameDayConversionPct",
    label: "Same Day 1st to 2nd Conversion %",
    type: "percentage",
    description:
      "Formerly “Same day conversion %”. (Same-day 1st→2nd conversions ÷ 1st consultations that day) × 100.",
    example: "10 first-time completions and 3 same-day second completions → 30%.",
  },
  ...sameDayTransitionColumns('Consultation', [2, 3, 4, 5, 6]),
  {
    key: "firstTimeRechargeCount",
    label: "1st Time Recharge Count",
    type: "number",
    metricGroupStart: true,
    description:
      "Number of first-ever wallet recharge transactions credited on this date.",
    example: "5 first-time top-ups → 5.",
  },
  {
    key: "firstTimeRechargeAmount",
    label: "1st Time Recharge Amount",
    type: "currency",
    description:
      "Total rupees from first-time recharge transactions on this date.",
    example: "Three first-time recharges of ₹100, ₹200, ₹100 → ₹400.",
  },
  {
    key: "secondRechargeCount",
    label: "2nd Recharge Count",
    type: "number",
    description:
      "Count of second-ever recharge transactions completed on this date.",
    example: "2 second-time top-ups → 2.",
  },
  {
    key: "secondRechargeAmount",
    label: "2nd Recharge Amount",
    type: "currency",
    description:
      "Total rupees from second-time recharges on this date.",
    example: "Two ₹500 second recharges → ₹1,000.",
  },
  {
    key: "sameDayFirstToSecondRechargeCount",
    label: "Same Day 1st to 2nd Recharge Count",
    type: "number",
    description:
      "Number of users whose lifetime 1st and 2nd recharge both happened on this date.",
    example: "4 users made both their 1st and 2nd recharge that day = 4.",
  },
  {
    key: "sameDayFirstToSecondRechargeConversionPct",
    label: "Same Day 1st to 2nd Recharge Conversion %",
    type: "percentage",
    description:
      "Users whose lifetime 1st and 2nd recharge both happened on this date, divided by users whose 1st recharge happened on this date.",
    example: "10 first rechargers and 4 also recharge a second time that day = 40%.",
  },
  {
    key: "sameDaySecondToThirdRechargeCount",
    label: "Same Day 2nd to 3rd Recharge Count",
    type: "number",
    description:
      "Number of users whose lifetime 2nd and 3rd recharge both happened on this date.",
    example: "2 users made both their 2nd and 3rd recharge that day = 2.",
  },
  {
    key: "sameDaySecondToThirdRechargeConversionPct",
    label: "Same Day 2nd to 3rd Recharge Conversion %",
    type: "percentage",
    description:
      "Users whose lifetime 2nd and 3rd recharge both happened on this date, divided by users whose 2nd recharge happened on this date.",
    example: "8 second rechargers and 2 also recharge a third time that day = 25%.",
  },
  ...sameDayTransitionColumns('Recharge', [3, 4, 5, 6]),
  {
    key: "thirdPlusRechargeCount",
    label: "≥3rd Recharge Count",
    type: "number",
    metricGroupStart: true,
    description:
      "Recharge transactions that are the customer’s 3rd-or-later lifetime recharge, credited on this date.",
    example: "Four 3rd+ top-ups → 4.",
  },
  {
    key: "thirdPlusRechargeAmount",
    label: "≥3rd Recharge Amount",
    type: "currency",
    description:
      "Total rupees from 3rd-or-later lifetime recharges on this date.",
    example: "₹300 + ₹700 from 3rd+ recharges → ₹1,000.",
  },
  ...pair(
    {
      key: "totalRecharge",
      label: "Total Recharge",
      type: "currency",
      metricGroupStart: true,
      description:
        "Sum of all wallet recharge amounts (all orders) credited on this date.",
      example: "Any top-up that day, all users → total ₹.",
    },
    {
      key: "uniqueTotalRechargeCount",
      label: "Unique Total Recharge Count",
      type: "number",
      description:
        "Number of distinct users who had at least one recharge credited on this date.",
      example: "₹10,000 total from 8 different users → Unique Total Recharge Count = 8.",
    }
  ),
  {
    key: "totalSpending",
    label: "Total Spending",
    type: "currency",
    omitOnMarketingDashboard: true,
    description:
      "Total rupees spent from wallet (consultations, etc.) on this date.",
    example: "Users spend ₹1,200 on paid sessions that day → ₹1,200.",
  },
];

/** Main dashboard: all columns. Marketing DASHBOARD: hides columns flagged with omitOnMarketingDashboard. */
export function getDailyBreakdownColumnDefinitions(isMarketingDashboard) {
  const visibleColumns = DAILY_BREAKDOWN_COLUMN_DEFINITIONS.filter(
    (c) => !c.inlineOnly
  );
  if (!isMarketingDashboard) return visibleColumns;
  return visibleColumns.filter((c) => !c.omitOnMarketingDashboard);
}

export function buildDailyBreakdownColumnVisibilityDefaults() {
  const vis = {};
  DAILY_BREAKDOWN_COLUMN_DEFINITIONS.forEach((col) => {
    vis[col.key] = true;
  });
  return vis;
}
